import type {
  Content,
  EnemyDef,
  LevelDef,
  PlanetDef,
  PlanetUpgradeDef,
  RegionDef,
  SlotLayer,
  StructureDef,
} from '@playnets/sim';
import { ENEMIES } from './enemies';
import { LEVELS } from './levels';
import { PLANETS } from './planets';
import { STRUCTURES } from './structures';
import { REGIONS } from './regions';
import { PLANET_UPGRADES } from './upgrades';
import {
  enemySchema,
  levelSchema,
  planetSchema,
  planetUpgradeSchema,
  regionSchema,
  structureSchema,
} from './schemas';

export * from './schemas';
export { PLANETS, V1_PLANETS } from './planets';
export { ENEMIES } from './enemies';
export { STRUCTURES } from './structures';
export { LEVELS, UNLOCKS, levelsForRegion } from './levels';
export { REGIONS, regionForPlanet } from './regions';
export { PLANET_UPGRADES } from './upgrades';
export { SYSTEM_PALETTES, paletteFor, type SystemPalette } from './palettes';

/**
 * Validate everything once, at the boundary. A bad hand-authored wave should
 * fail here with a path, not forty seconds into a level.
 */
function validate(): void {
  for (const p of PLANETS) planetSchema.parse(p);
  for (const e of ENEMIES) enemySchema.parse(e);
  for (const s of STRUCTURES) structureSchema.parse(s);
  for (const l of LEVELS) levelSchema.parse(l);
  for (const r of REGIONS) regionSchema.parse(r);
  for (const u of PLANET_UPGRADES) planetUpgradeSchema.parse(u);

  // Referential integrity the schemas can't express on their own.
  const enemyIds = new Set(ENEMIES.map((e) => e.id));
  const structureIds = new Set(STRUCTURES.map((s) => s.id));
  const levelIds = new Set(LEVELS.map((l) => l.id));
  const regionIds = new Set(REGIONS.map((r) => r.id));
  const planetIds = new Set(PLANETS.map((p) => p.id));

  for (const r of REGIONS) {
    if (!planetIds.has(r.homePlanet)) {
      throw new Error(`region ${r.id} is home to unknown planet ${r.homePlanet}`);
    }
    for (const e of [...r.roster, r.boss]) {
      if (!enemyIds.has(e)) throw new Error(`region ${r.id} rosters unknown enemy ${e}`);
    }
  }

  for (const e of ENEMIES) {
    if (e.splitInto && !enemyIds.has(e.splitInto)) {
      throw new Error(`enemy ${e.id} splits into unknown ${e.splitInto}`);
    }
    if (e.spawns && !enemyIds.has(e.spawns)) {
      throw new Error(`enemy ${e.id} spawns unknown ${e.spawns}`);
    }
  }
  for (const p of PLANETS) {
    if (p.exclusive && !structureIds.has(p.exclusive)) {
      throw new Error(`planet ${p.id} claims unknown exclusive ${p.exclusive}`);
    }
  }
  for (const l of LEVELS) {
    if (!regionIds.has(l.region)) {
      throw new Error(`level ${l.id} sits in unknown region ${l.region}`);
    }
    for (const n of l.next) {
      if (!levelIds.has(n)) throw new Error(`level ${l.id} points at unknown ${n}`);
    }
    for (const w of l.waves) {
      for (const g of w.groups) {
        if (!enemyIds.has(g.enemy)) {
          throw new Error(`level ${l.id} wave ${w.id} uses unknown enemy ${g.enemy}`);
        }
      }
    }
  }
}

let validated = false;

/** Build the content bundle the sim consumes. Idempotent. */
export function loadContent(): Content {
  if (!validated) {
    validate();
    validated = true;
  }
  return {
    planets: new Map<string, PlanetDef>(PLANETS.map((p) => [p.id, p])),
    enemies: new Map<string, EnemyDef>(ENEMIES.map((e) => [e.id, e])),
    structures: new Map<string, StructureDef>(STRUCTURES.map((s) => [s.id, s])),
    levels: new Map<string, LevelDef>(LEVELS.map((l) => [l.id, l])),
    regions: new Map<string, RegionDef>(REGIONS.map((r) => [r.id, r])),
    upgrades: new Map<string, PlanetUpgradeDef>(PLANET_UPGRADES.map((u) => [u.id, u])),
  };
}

/**
 * Structures this planet is allowed to build on this kind of mount. Takes the
 * catalogue, not the layer — stations share the ring's list, so callers pass
 * `slotBuildLayer(slot)` rather than the slot's own layer.
 */
export function structuresFor(planetId: string, layer: 'surface' | 'ring'): StructureDef[] {
  return STRUCTURES.filter(
    (s) =>
      (s.layer === layer || s.layer === 'both') &&
      (!s.exclusiveTo || s.exclusiveTo === planetId),
  );
}
